'use client';

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { EditorInstance } from 'novel';
import { defaultExtensions } from './extensions';
import { suggestionItems, getFilteredBaseItems, SuggestionItem } from './components/slash-command';
import { ChangeIndicator } from './extensions/change-indicator';
import './extensions/change-indicator.css';
import './extensions/editable-image.css';
import './MarkdownEditor.css';
import { JSONContent } from '@tiptap/react';

// Import markdown conversion libraries
import { marked } from 'marked';
import { generateJSON } from '@tiptap/html';
import { configureMarkedWithImageSize } from './extensions/image-size-extension';

// Import our custom components and hooks
import { useImageUpload } from './hooks/useImageUpload';
import { useAICommandManager } from './hooks/useAICommandManager';
import { countWords, getFormattedContent } from './utils/content-formatter';
import { getEditorConfig } from './components/editor-config';
import AICommandDialog from './components/ai-command-dialog';
import { EditorCore, EditorCoreRef } from './components/EditorCore';
import AssetBrowserModal from '../assets/AssetBrowserModal';
import AgentChatPanel, { AgentChatPanelRef } from '@/app/articles/components/AgentChatPanel';
import { ChatMessage } from '@contentgrowth/llm-service/ui/react';
import { articlesApi } from '@/app/api/articles';
import { getPublicAssetUrl } from '@/utils/assetUrl';

import { CommandShortcut } from './extensions/command-shortcut';
import { useChat } from '@/hooks/useChat';
import { EditArticleResponse } from '@/app/api/article-ai';

// Utility functions for performance optimization
function throttle<T extends (...args: any[]) => any>(func: T, limit: number): T {
  let inThrottle: boolean;
  return function (this: any, ...args: any[]) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  } as T;
}

function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T {
  let timeoutId: NodeJS.Timeout;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  } as T;
}

interface EditorProps {
  articleId?: string;
  articleTitle?: string;
  initialContent?: string | JSONContent;
  onChange?: (content: string) => void;
  onSave?: (content: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  format?: 'markdown' | 'html' | 'json';
  isAgentMode?: boolean;
  isReadOnly?: boolean;
  onToggleAgentMode?: () => void;
  isChatPanelVisible?: boolean;
  onToggleChatPanel?: () => void;
  onAiEdit?: (instruction: string, messages?: ChatMessage[], selection?: { text: string; prefix: string; suffix: string } | null, cursorContext?: { before: string; after: string; currentParagraph: string; paragraphBefore: string; paragraphAfter: string; isEmptyParagraph: boolean; } | null, signal?: AbortSignal) => Promise<{ content: string; message: string; hasContentChanges: boolean; oldContent: string; }>;
  onBlur?: (content: string) => void;
  onUnsavedChange?: (hasUnsaved: boolean) => void;
  onContentUpdate?: (newContent: string, previousContent?: string, changeSummary?: string) => void;
  previousContent?: string;
  isAiProcessing?: boolean;
  // History management props to pass through to AgentChatPanel
  canUndo?: boolean;
  canRedo?: boolean;
  lastChangeSummary?: string;
  nextChangeSummary?: string;
  onUndo?: () => void;
  onRedo?: () => void;
  onReset?: () => void;
  manualEdit?: { oldContent: string; newContent: string } | null;
  // Ref for direct access to EditorCore functions (highlightAndScrollTo, updateEditorContent)
  editorCoreRef?: React.RefObject<EditorCoreRef>;
}

// Re-export EditorCoreRef for external use
export type { EditorCoreRef };

export const MarkdownEditor: React.FC<EditorProps> = ({
  articleId,
  articleTitle,
  initialContent = '',
  onChange,
  onSave,
  placeholder = 'Start writing... (Type / for commands)',
  className = '',
  format = 'markdown',
  isAgentMode = false,
  isReadOnly = false,
  onToggleAgentMode,
  isChatPanelVisible,
  onToggleChatPanel,
  onAiEdit,
  onBlur,
  onUnsavedChange,
  onContentUpdate,
  previousContent = '',
  isAiProcessing = false,
  canUndo,
  canRedo,
  lastChangeSummary,
  nextChangeSummary,
  onUndo,
  onRedo,
  onReset,
  manualEdit,
  editorCoreRef,
}) => {
  // State for UI elements
  const [saveStatus, setSaveStatus] = useState('Saved');
  const [wordCount, setWordCount] = useState<number | null>(null);

  // Set up a ref to measure the editor container
  const editorContainerRef = useRef<HTMLDivElement>(null);



  // our fixed toolbar is sticky now, so we usu. don't need the bubble commands
  const [isAssetBrowserOpen, setIsAssetBrowserOpen] = useState(false);

  // Track whether change indicators are currently active (showing AI changes)
  // This ref stores the activator function from EditorCore
  const activateChangeIndicatorsRef = useRef<(() => void) | null>(null);

  // Editor instance (still needed for useImageUpload hook and save logic)
  const [editorInstance, setEditorInstance] = useState<EditorInstance | null>(null);

  // NOTE: Selection/cursor context is now tracked internally by EditorCore


  // Image upload hook
  const { isUploadingImage, handleAssetSelect, handleImagePaste } = useImageUpload({ editorInstance });

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (wordCountTimeoutRef.current) {
        clearTimeout(wordCountTimeoutRef.current);
      }
      if (contentUpdateTimeoutRef.current) {
        clearTimeout(contentUpdateTimeoutRef.current);
      }
    };
  }, []);

  const scheduleEditorUpdate = useCallback((editor: EditorInstance) => {
    // Clear any existing timeout
    if (contentUpdateTimeoutRef.current) {
      clearTimeout(contentUpdateTimeoutRef.current);
    }

    // PERFORMANCE: Combine word count and content update into single debounced operation
    // This extracts markdown ONCE instead of twice, reducing operations by ~33%
    contentUpdateTimeoutRef.current = setTimeout(() => {
      // Extract content once and use for both operations
      const currentContent = getFormattedContent(editor, format) as string;

      // Update word count
      setWordCount(countWords(editor.getText()));

      // Update content if onChange callback exists
      if (onChange) {
        latestContentRef.current = currentContent;
        onChange(currentContent);
      }
    }, 300); // 300ms debounce (balanced for responsiveness)
  }, [format, onChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (contentUpdateTimeoutRef.current) {
        clearTimeout(contentUpdateTimeoutRef.current);
      }
    };
  }, []);

  // Track content changes for save on blur
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const lastSavedContentRef = useRef(initialContent);
  const latestContentRef = useRef<string | null>(
    typeof initialContent === 'string' ? initialContent : null
  );
  const wordCountTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const contentUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (onUnsavedChange) {
      onUnsavedChange(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onUnsavedChange]);

  // Ref for AgentChatPanel to access its sendMessage function
  const chatPanelRef = useRef<AgentChatPanelRef>(null);

  // Save handler for the toolbar save button
  const handleContentSave = useCallback(async (blurring: boolean = false) => {
    // Check using ref method (source of truth in EditorCore)
    const unsaved = editorCoreRef?.current?.hasUnsavedChanges() ?? hasUnsavedChanges;
    console.log('[MarkdownEditor] handleContentSave called', { blurring, unsaved, hasEditorInstance: !!editorInstance });
    if (!editorInstance || !unsaved) return;

    const currentContent = getFormattedContent(editorInstance, format) as string;

    // In agent mode, always call onBlur to trigger manual edit tracking
    // This ensures both Ctrl+S and blur save create "manual edit" entries in chat
    if (onBlur && isAgentMode) {
      console.log('[MarkdownEditor] Calling onBlur (handleContentBlur)', { blurring });
      onBlur(currentContent);
    }

    // If not blurring (Ctrl+S), refocus the editor
    if (!blurring) {
      editorInstance?.chain().focus();
    }

    try {
      setSaveStatus('Saving...');
      await articlesApi.saveContent(articleId || '', currentContent);
      lastSavedContentRef.current = currentContent;
      // Clear via ref method (EditorCore is source of truth)
      editorCoreRef?.current?.clearUnsavedChanges();
      setHasUnsavedChanges(false); // Keep local state in sync for callbacks
      setSaveStatus('Saved');
    } catch (error) {
      console.error('Failed to save content:', error);
      setSaveStatus('Save failed');
    }
  }, [editorInstance, hasUnsavedChanges, isAgentMode, onBlur, format, onSave, articleId, editorCoreRef]);

  // Note: Smart blur is now handled internally by EditorCore via handleDomBlur

  // Listen for chat input focus to trigger save
  useEffect(() => {
    const handleChatInputFocus = () => {
      if (hasUnsavedChanges && editorInstance) {
        console.log('[MarkdownEditor] Chat input focused, triggering save');
        handleContentSave(true);
      }
    };

    // Listen for focus on chat input (use capture phase to catch it early)
    const chatInputSelector = '[data-chat-input], .chat-input, textarea[placeholder*="Ask"]';
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (target.matches && target.matches(chatInputSelector)) {
        handleChatInputFocus();
      }
    };

    document.addEventListener('focusin', handleFocusIn, true);
    return () => {
      document.removeEventListener('focusin', handleFocusIn, true);
    };
  }, [hasUnsavedChanges, editorInstance, handleContentSave]);

  // Effect to update editor content when initialContent prop changes
  // Effect to update editor content when initialContent prop changes
  useEffect(() => {
    if (editorInstance && typeof initialContent === 'string') {
      // Check if content is actually different to avoid unnecessary updates and cursor resets
      const currentContent = getFormattedContent(editorInstance, format) as string;
      if (currentContent === initialContent) {
        return;
      }

      // Configure marked with image size extension
      configureMarkedWithImageSize();

      // Convert markdown to HTML, then generate JSON for the editor
      const html = marked.parse(initialContent) as string;
      const jsonContent = generateJSON(html, defaultExtensions);

      // Set the new content, but don't trigger the onChange callback
      // maintain cursor position if possible, though replacing content usually resets it
      editorInstance.commands.setContent(jsonContent, false);
    }
  }, [editorInstance, initialContent, format]);

  // Function to normalize initial content to JSONContent
  const getInitialContent = useCallback((): JSONContent => {
    if (typeof initialContent === 'object') {
      return initialContent as JSONContent;
    }

    if (!initialContent || (typeof initialContent === 'string' && initialContent.trim() === '')) {
      return {
        type: 'doc',
        content: [{ type: 'paragraph' }]
      };
    }

    // If format is markdown, convert markdown to HTML first, then to JSON
    if (format === 'markdown') {
      try {
        // Configure marked with image size extension
        configureMarkedWithImageSize();

        // Convert markdown to HTML
        const html = marked.parse(initialContent as string) as string;
        // Convert HTML to JSON
        return generateJSON(html, defaultExtensions);
      } catch (error) {
        console.error('Error converting markdown to JSON:', error);
      }
    }

    // Fallback or if format is not markdown
    return {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [{ type: 'text', text: initialContent as string }]
        }
      ]
    };
  }, [initialContent, format]);


  // Initialize AI command manager
  const {
    aiDialogOpen,
    aiCommandType,
    aiCommandLoading,
    aiService,
    articleTitle: editorArticleTitle,
    openAiCommandDialog,
    closeAiCommandDialog,
    handleAiCommandSubmit,
    handleAcceptResult
  } = useAICommandManager(editorInstance, articleTitle);

  // Note: Keyboard shortcuts are now handled internally by EditorCore via useKeyboardShortcuts hook

  // Track if any dialog is open (for unified refocus handling)
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);

  // Listen for link dialog state changes from FixedToolbar
  useEffect(() => {
    const handleLinkDialogOpen = () => setIsLinkDialogOpen(true);
    const handleLinkDialogClose = () => setIsLinkDialogOpen(false);

    document.addEventListener('link-dialog-opened', handleLinkDialogOpen);
    document.addEventListener('link-dialog-closed', handleLinkDialogClose);

    return () => {
      document.removeEventListener('link-dialog-opened', handleLinkDialogOpen);
      document.removeEventListener('link-dialog-closed', handleLinkDialogClose);
    };
  }, []);

  // Create extensions array with change indicator - defined early to avoid hoisting issues
  const getExtensions = useCallback(() => {
    const extensions = [...defaultExtensions, CommandShortcut];

    // Always add change indicators in agent mode where we track AI changes
    if (isAgentMode) {
      extensions.push(
        ChangeIndicator.configure({
          previousContent, // Initial value
          enabled: true,
        }) as any
      );
    }

    return extensions;
  }, [isAgentMode]); // PERFORMANCE: Removed previousContent from deps to avoid re-creating extensions

  // PERFORMANCE: Update extension options dynamically when previousContent changes
  // This avoids re-initializing all extensions (which is expensive)
  useEffect(() => {
    if (!editorInstance || !isAgentMode) return;

    const changeIndicatorExt = editorInstance.extensionManager.extensions.find(
      ext => ext.name === 'changeIndicator'
    );

    if (changeIndicatorExt) {
      changeIndicatorExt.options.previousContent = previousContent;
    }
  }, [editorInstance, previousContent, isAgentMode]);


  // Listen for custom events from slash commands
  useEffect(() => {
    const handleAiCommandDialogEvent = (event: CustomEvent) => {
      const { commandType } = event.detail;

      // Get the current selection if any
      openAiCommandDialog(commandType);
    };

    // Add event listener
    document.addEventListener('ai-command-dialog', handleAiCommandDialogEvent as EventListener);

    // Clean up
    return () => {
      document.removeEventListener('ai-command-dialog', handleAiCommandDialogEvent as EventListener);
    };
  }, [openAiCommandDialog]);

  // Listen for asset browser slash command
  useEffect(() => {
    const handleOpenAssetBrowser = () => {
      setIsAssetBrowserOpen(true);
    };
    document.addEventListener('open-asset-browser', handleOpenAssetBrowser);

    return () => {
      document.removeEventListener('open-asset-browser', handleOpenAssetBrowser);
    };
  }, []);

  // Listen for image dialog slash command (old URL-based)
  useEffect(() => {
    const handleOpenImageDialog = () => {
      // We need to trigger the image dialog from the fixed toolbar
      // Dispatch another event that the toolbar can listen to
      const toolbarEvent = new CustomEvent('toolbar-open-image-dialog');
      document.dispatchEvent(toolbarEvent);
    };

    document.addEventListener('open-image-dialog', handleOpenImageDialog);

    return () => {
      document.removeEventListener('open-image-dialog', handleOpenImageDialog);
    };
  }, []);

  // Listen for AI-powered image dialog slash command
  useEffect(() => {
    const handleOpenAIImageDialog = () => {
      openAiCommandDialog('generate-image');
    };

    document.addEventListener('open-ai-image-dialog', handleOpenAIImageDialog);

    return () => {
      document.removeEventListener('open-ai-image-dialog', handleOpenAIImageDialog);
    };
  }, [openAiCommandDialog]);

  // Listen for image editing (double-click on images)
  useEffect(() => {
    const handleEditImage = (event: CustomEvent) => {
      const { imageData, position } = event.detail;

      // Store the position for updating the image later
      const editEvent = new CustomEvent('toolbar-edit-image-dialog', {
        detail: { imageData, position }
      });
      document.dispatchEvent(editEvent);
    };

    document.addEventListener('edit-image', handleEditImage as EventListener);

    return () => {
      document.removeEventListener('edit-image', handleEditImage as EventListener);
    };
  }, []);

  // Update CSS variables when the editor size or position changes
  useEffect(() => {
    const updateEditorDimensions = () => {
      if (editorContainerRef.current) {
        const rect = editorContainerRef.current.getBoundingClientRect();
        document.documentElement.style.setProperty('--editor-width', `${rect.width}px`);
        document.documentElement.style.setProperty('--editor-left', `${rect.left}px`);
      }
    };

    // PERFORMANCE: Throttle dimension updates to max once per 100ms
    // Reduces overhead from high-frequency scroll/resize events by ~90%
    const throttledUpdate = throttle(updateEditorDimensions, 100);

    // Update dimensions initially, on resize, and on scroll
    updateEditorDimensions();
    window.addEventListener('resize', throttledUpdate);
    window.addEventListener('scroll', throttledUpdate, { passive: true });

    // Create a ResizeObserver to detect size changes of the editor container
    const resizeObserver = new ResizeObserver(() => {
      updateEditorDimensions();
    });

    if (editorContainerRef.current) {
      resizeObserver.observe(editorContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateEditorDimensions);
      window.removeEventListener('scroll', updateEditorDimensions);
      resizeObserver.disconnect();
    };
  }, []);

  // --- Centralized Chat Logic (Hoisted from AgentChatPanel) ---

  const handleChatSubmit = useCallback(async (currentMessages: ChatMessage[], signal?: AbortSignal) => {
    const lastMessage = currentMessages[currentMessages.length - 1];
    if (!lastMessage || lastMessage.role !== 'user') return null;
    const chatHistory = currentMessages.slice(0, -1);

    // Filter out system messages from history if needed, or backend handles it
    // onAiEdit is passed from ArticleEditor
    if (!onAiEdit) return null;

    return onAiEdit(
      lastMessage.content,
      chatHistory,
      editorCoreRef?.current?.getSelectionContext(),
      editorCoreRef?.current?.getCursorContext(),
      signal
    );
  }, [onAiEdit, editorCoreRef]);

  const handleChatResponse = useCallback((response: EditArticleResponse & { oldContent: string; rawContent?: string }) => {
    const { content: newContent, message, oldContent, hasContentChanges, changeSummary, rawContent } = response;
    const partialMessage: Partial<ChatMessage> = { content: message };

    // Preserve raw content for history fidelity
    if (rawContent) {
      partialMessage.rawContent = rawContent;
    }

    if (hasContentChanges && newContent && onContentUpdate) {
      // Pass both new content, previous content, and changeSummary for proper history management
      onContentUpdate(newContent, oldContent, changeSummary);
      if (oldContent) {
        partialMessage.diff = { oldContent, newContent };
      }
    }
    return partialMessage;
  }, [onContentUpdate]);

  const {
    messages: chatMessages,
    setMessages: setChatMessages,
    input: chatInput,
    handleInputChange: handleChatInputChange,
    handleSubmit: handleChatSubmitForm,
    isLoading: isChatLoading,
    error: chatError,
    sendMessage: chatSendMessage,
    stop: chatStop
  } = useChat({
    apiEndpoint: undefined, // we use handler
    handler: handleChatSubmit,
    onResponse: handleChatResponse,
    // Note: historyEndpoint not used - chat history is session-based only
    enabled: isAgentMode // Only load/enable when in agent mode
  });

  // Manual Edit tracking - injection into chat history
  const lastProcessedEditRef = useRef(manualEdit);
  useEffect(() => {
    if (manualEdit && manualEdit !== lastProcessedEditRef.current) {
      const manualEditMessage: ChatMessage = {
        id: `manual-edit-${Date.now()}`,
        role: 'system',
        content: 'Manual edit detected.',
        diff: manualEdit,
      };
      setChatMessages(prevMessages => [...prevMessages, manualEditMessage]);
      lastProcessedEditRef.current = manualEdit;
    }
  }, [manualEdit, setChatMessages]);

  // Undo/Redo tracking - injection into chat history
  // Since onUndo/onRedo are controlled by parent, we wrap them or detect changes?
  // We can inject messages when onUndo is CALLED on the buttons we render (toolbar/panel).
  // But keyboard shortcuts might bypass this.
  // Ideally, ArticleEditor should manage this, but we are in MarkdownEditor.
  // The AgentChatPanel had handleUndo wrapping onUndo. We can pass wrapped handlers to AgentChatPanel.
  const handleUndoWithChat = useCallback(() => {
    if (onUndo) {
      onUndo();
      const undoMessage: ChatMessage = {
        id: `system-undo-${Date.now()}`,
        role: 'system',
        content: `SYSTEM: The user has undone the last change: '${lastChangeSummary || 'edit'}'. The content has been reverted.`,
      };
      setChatMessages(prev => [...prev, undoMessage]);
    }
  }, [onUndo, lastChangeSummary, setChatMessages]);

  const handleRedoWithChat = useCallback(() => {
    if (onRedo) {
      onRedo();
      const redoMessage: ChatMessage = {
        id: `system-redo-${Date.now()}`,
        role: 'system',
        content: `SYSTEM: The user has redone the last change: '${nextChangeSummary || 'edit'}'. The content has been restored.`,
      };
      setChatMessages(prev => [...prev, redoMessage]);
    }
  }, [onRedo, nextChangeSummary, setChatMessages]);

  const handleResetWithChat = useCallback(() => {
    if (confirm('Are you sure you want to discard all changes and reset the content? This action cannot be undone.')) {
      if (onReset) {
        onReset();
      }
      setChatMessages([]);
    }
  }, [onReset, setChatMessages]);

  if (isAgentMode) {
    return (
      <div
        className={`novel-editor-wrapper ${className} h-full w-full`}
        id="markdown-editor-container"
        ref={editorContainerRef}
      >
        {/* Content area - takes full width of parent */}
        <div className="flex flex-col w-full bg-white shadow-lg h-full">
          {/* Editor and Chat Panel */}
          <PanelGroup direction="horizontal" className="flex flex-grow">
            {/* Editor Section */}
            <Panel
              defaultSize={isChatPanelVisible ? 75 : 100}
              minSize={50}
              className="flex flex-col overflow-hidden relative"
            >
              <div className="flex flex-col h-full w-full relative">
                <EditorCore
                  ref={editorCoreRef}
                  editorInstance={editorInstance}
                  setEditorInstance={setEditorInstance}
                  isReadOnly={isReadOnly}
                  isAiProcessing={isAiProcessing}
                  getInitialContent={getInitialContent}
                  getExtensions={getExtensions}
                  format={format}
                  handleImagePaste={handleImagePaste}
                  onContentUpdate={scheduleEditorUpdate}
                  onUnsavedChanges={setHasUnsavedChanges}
                  onSaveStatusChange={setSaveStatus}
                  onAutoSave={() => handleContentSave(true)}
                  onChangeIndicatorsActivate={(activateFn) => { activateChangeIndicatorsRef.current = activateFn; }}
                  onAiEdit={onAiEdit}
                  chatPanelSendMessage={async (message: string) => {
                    console.log('[MarkdownEditor] Using hoisted chatSendMessage');
                    return chatSendMessage(message);
                  }}
                  suggestionItems={suggestionItems}
                  saveStatus={saveStatus}
                  wordCount={wordCount}
                  isUploadingImage={isUploadingImage}
                  isAgentMode={isAgentMode}
                  onToggleAgentMode={onToggleAgentMode}
                  onInsertAsset={() => setIsAssetBrowserOpen(true)}
                  onSave={() => handleContentSave(false)}
                  onBlurSave={() => handleContentSave(true)}
                  onAICommand={openAiCommandDialog}
                  previousContent={previousContent}
                />
              </div>
            </Panel>

            {/* Chat Panel - Resizable */}
            {isAgentMode && isChatPanelVisible && articleId && (
              <>
                <PanelResizeHandle className="w-2 bg-gray-300 hover:bg-blue-500 transition-colors cursor-col-resize border-l border-r border-gray-400" />
                <Panel defaultSize={30} minSize={20} maxSize={40} className="flex flex-col">
                  <AgentChatPanel
                    ref={chatPanelRef}
                    articleId={articleId}
                    // Pass chat state
                    chatState={{
                      messages: chatMessages,
                      setMessages: setChatMessages,
                      input: chatInput,
                      handleInputChange: handleChatInputChange,
                      handleSubmit: handleChatSubmitForm,
                      isLoading: isChatLoading,
                      error: chatError,
                      sendMessage: chatSendMessage,
                      stop: chatStop
                    }}
                    // Pass wrapped handlers
                    onUndoClick={handleUndoWithChat}
                    onRedoClick={handleRedoWithChat}
                    onResetClick={handleResetWithChat}
                    // Pass history props
                    canUndo={canUndo}
                    canRedo={canRedo}
                    lastChangeSummary={lastChangeSummary}
                    nextChangeSummary={nextChangeSummary}
                    disabled={isReadOnly}
                    disabledReason={isReadOnly ? "Content Copilot chat is disabled while editing the article title. Please save or cancel title editing first." : undefined}
                    disableAutoFocus={!isChatPanelVisible || isAiProcessing || isLinkDialogOpen || isAssetBrowserOpen || aiDialogOpen || isReadOnly}
                  />
                </Panel>
              </>
            )}
          </PanelGroup>
        </div>

        {/* AI Command Dialog */}
        <AICommandDialog
          isOpen={aiDialogOpen}
          onClose={() => {
            closeAiCommandDialog();
          }}
          onSubmit={handleAiCommandSubmit}
          onAccept={handleAcceptResult}
          commandType={aiCommandType}
          initialContent={typeof initialContent === 'string' ? initialContent : ''}
          hasSelection={!!editorCoreRef?.current?.getSelectionContext()?.text}
          selectedText={editorCoreRef?.current?.getSelectionContext()?.text || ''}
          aiService={aiService} // Pass the AICommandService instance
          articleId={articleId} // Pass the article ID for image generation context
          articleTitle={editorArticleTitle} // Pass the article title
        />
      </div >
    );
  }

  return (
    <div
      className={`novel-editor-wrapper ${className}`}
      id="markdown-editor-container"
      ref={editorContainerRef}
    >
      <EditorCore
        ref={editorCoreRef}
        editorInstance={editorInstance}
        setEditorInstance={setEditorInstance}
        isReadOnly={isReadOnly}
        isAiProcessing={isAiProcessing}
        getInitialContent={getInitialContent}
        getExtensions={getExtensions}
        format={format}
        handleImagePaste={handleImagePaste}
        onContentUpdate={scheduleEditorUpdate}
        onUnsavedChanges={setHasUnsavedChanges}
        onSaveStatusChange={setSaveStatus}
        onAutoSave={() => handleContentSave(true)}
        onChangeIndicatorsActivate={(activateFn) => { activateChangeIndicatorsRef.current = activateFn; }}
        onAiEdit={onAiEdit}
        chatPanelSendMessage={async (message: string) => chatSendMessage(message)}
        suggestionItems={suggestionItems}
        saveStatus={saveStatus}
        wordCount={wordCount}
        isUploadingImage={isUploadingImage}
        isAgentMode={isAgentMode}
        onToggleAgentMode={onToggleAgentMode}
        onInsertAsset={() => setIsAssetBrowserOpen(true)}
        onSave={() => handleContentSave(false)}
        onBlurSave={() => handleContentSave(true)}
        onAICommand={openAiCommandDialog}
        previousContent={previousContent}
      />

      {/* AI Command Dialog */}
      <AICommandDialog
        isOpen={aiDialogOpen}
        onClose={() => {
          closeAiCommandDialog();
        }}
        onSubmit={handleAiCommandSubmit}
        onAccept={handleAcceptResult}
        commandType={aiCommandType}
        initialContent={typeof initialContent === 'string' ? initialContent : ''}
        hasSelection={!!editorCoreRef?.current?.getSelectionContext()?.text}
        selectedText={editorCoreRef?.current?.getSelectionContext()?.text || ''}
        aiService={aiService}
        articleId={articleId}
        articleTitle={editorArticleTitle}
      />

      <AssetBrowserModal
        isOpen={isAssetBrowserOpen}
        onClose={() => setIsAssetBrowserOpen(false)}
        onSelectAsset={handleAssetSelect}
      />

    </div>
  );
};

